/**
 * Wardrobe feature hooks
 * Handles wardrobe items CRUD, onboarding, set detection, pods, and favorites
 */

"use client";

import { useState, useCallback, useEffect } from "react";
import { useUser, triggerCreditRefresh } from "@/shared/contexts/UserContext";
import {
  getWardrobeItems,
  getWardrobeStats,
  getWardrobeItem,
  deleteWardrobeItem,
  updateWardrobeItem,
  onboardClothes,
  onboardClothesBatch,
  analyzeForSet,
  confirmOnboard,
  getUploadQuota,
  getPods,
  ensureDefaultPods,
  getPod,
  createPod as apiCreatePod,
  renamePod as apiRenamePod,
  deletePod as apiDeletePod,
  addItemToPod as apiAddItemToPod,
  removeItemFromPod as apiRemoveItemFromPod,
  toggleFavorite as apiToggleFavorite,
  getCatalogBasics,
  commitCatalogSelection as apiCommitCatalogSelection,
} from "@/features/wardrobe/api";
import { isApiSuccess, getApiError } from "@/shared/lib/api";
import type {
  WardrobeItem,
  WardrobeItemsParams,
  WardrobeItemsResponse,
  WardrobeStatsResponse,
  WardrobeOnboardResponse,
  WardrobeUploadJobResponse,
  AnalyzeForSetResponse,
  ConfirmOnboardJobResponse,
  Pod,
  PodWithItems,
  CatalogBasicsParams,
  CatalogBasicsResponse,
  CatalogCommitItem,
  CatalogCommitResponse,
} from "@/features/wardrobe/types";
import type { UploadQuota } from "@/shared/api/types";
import { trackEvent, trackError, trackDressDeleted, trackDressFavorited, trackDressWorn, trackPodCreated, trackPodItemAdded, trackPodItemRemoved } from "@/shared/lib/analytics";

// ============================================
// Generic Hook State
// ============================================

interface UseApiState<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
}

interface UseApiReturn<T> extends UseApiState<T> {
  refetch: () => Promise<void>;
}

interface UseMutationReturn<TRequest, TResponse> {
  data: TResponse | null;
  loading: boolean;
  error: string | null;
  mutate: (request: TRequest) => Promise<TResponse | null>;
  reset: () => void;
}

// Cross-component invalidation. Any wardrobe/pod mutation fires this event so
// the dashboard (and other listeners) can refetch without remounting. Used by
// useDashboardData to keep pod cover thumbnails in sync after edits made on
// other pages.
export const WARDROBE_MUTATED_EVENT = "hoku:wardrobeMutated";
function emitWardrobeMutated() {
  if (typeof window !== "undefined") {
    window.dispatchEvent(new Event(WARDROBE_MUTATED_EVENT));
  }
}

// ============================================
// Wardrobe Items Hooks
// ============================================

/**
 * Hook to fetch user's wardrobe items (paginated)
 */
export function useWardrobeItems(
  params?: WardrobeItemsParams
): UseApiReturn<WardrobeItemsResponse> {
  const [state, setState] = useState<UseApiState<WardrobeItemsResponse>>({
    data: null,
    loading: false,
    error: null,
  });
  const [hasFetched, setHasFetched] = useState(false);

  // Extract primitive values for stable dependencies
  const limit = params?.limit;
  const offset = params?.offset;
  const category = params?.category;

  const refetch = useCallback(async () => {
    setState((prev) => ({ ...prev, loading: true, error: null }));

    const response = await getWardrobeItems({ limit, offset, category });

    if (isApiSuccess(response)) {
      setState({ data: response.data, loading: false, error: null });
    } else {
      setState({ data: null, loading: false, error: getApiError(response) });
    }
  }, [limit, offset, category]);

  // Only auto-fetch once on mount
  useEffect(() => {
    if (!hasFetched) {
      setHasFetched(true);
      refetch();
    }
  }, [hasFetched, refetch]);

  return { ...state, refetch };
}

/**
 * Hook to fetch wardrobe statistics
 */
export function useWardrobeStats(): UseApiReturn<WardrobeStatsResponse> {
  const [state, setState] = useState<UseApiState<WardrobeStatsResponse>>({
    data: null,
    loading: false,
    error: null,
  });
  const [hasFetched, setHasFetched] = useState(false);

  const refetch = useCallback(async () => {
    setState((prev) => ({ ...prev, loading: true, error: null }));

    const response = await getWardrobeStats();

    if (isApiSuccess(response)) {
      setState({ data: response.data, loading: false, error: null });
    } else {
      setState({ data: null, loading: false, error: getApiError(response) });
    }
  }, []);

  // Only auto-fetch once on mount
  useEffect(() => {
    if (!hasFetched) {
      setHasFetched(true);
      refetch();
    }
  }, [hasFetched, refetch]);

  return { ...state, refetch };
}

/**
 * Hook to fetch a specific wardrobe item by ID
 */
export function useWardrobeItem(id: string | null): UseApiReturn<WardrobeItem> {
  const [state, setState] = useState<UseApiState<WardrobeItem>>({
    data: null,
    loading: false,
    error: null,
  });

  const refetch = useCallback(async () => {
    if (!id) return;

    setState({ data: null, loading: true, error: null });

    const response = await getWardrobeItem(id);

    if (isApiSuccess(response)) {
      setState({ data: response.data, loading: false, error: null });
    } else {
      setState({ data: null, loading: false, error: getApiError(response) });
    }
  }, [id]);

  useEffect(() => {
    if (id) {
      refetch();
    }
  }, [refetch, id]);

  return { ...state, refetch };
}

/**
 * Hook to delete a wardrobe item
 */
export function useDeleteWardrobeItem(): UseMutationReturn<string, { deleted: boolean }> {
  const [state, setState] = useState<UseApiState<{ deleted: boolean }>>({
    data: null,
    loading: false,
    error: null,
  });

  const mutate = useCallback(async (id: string) => {
    setState({ data: null, loading: true, error: null });

    const response = await deleteWardrobeItem(id);

    // Backend returns { success: true, message: '...' } without data field
    // So we check for success and return { deleted: true }
    if (response && (response as { success?: boolean }).success === true) {
      trackDressDeleted();
      emitWardrobeMutated();
      const result = { deleted: true };
      setState({ data: result, loading: false, error: null });
      return result;
    } else {
      setState({ data: null, loading: false, error: getApiError(response) });
      return null;
    }
  }, []);

  const reset = useCallback(() => {
    setState({ data: null, loading: false, error: null });
  }, []);

  return { ...state, mutate, reset };
}

/**
 * Hook to update a wardrobe item (hasWorn, isFavorite)
 */
export function useUpdateWardrobeItem(): UseMutationReturn<
  { id: string; hasWorn?: boolean; isFavorite?: boolean },
  WardrobeItem
> {
  const [state, setState] = useState<UseApiState<WardrobeItem>>({
    data: null,
    loading: false,
    error: null,
  });

  const mutate = useCallback(async (request: { id: string; hasWorn?: boolean; isFavorite?: boolean }) => {
    setState({ data: null, loading: true, error: null });

    const response = await updateWardrobeItem(request.id, {
      hasWorn: request.hasWorn,
      isFavorite: request.isFavorite,
    });

    if (isApiSuccess(response)) {
      if (request.hasWorn !== undefined) trackDressWorn(request.id);
      if (request.isFavorite !== undefined) trackDressFavorited(request.id, request.isFavorite);
      setState({ data: response.data, loading: false, error: null });
      return response.data;
    } else {
      setState({ data: null, loading: false, error: getApiError(response) });
      return null;
    }
  }, []);

  const reset = useCallback(() => {
    setState({ data: null, loading: false, error: null });
  }, []);

  return { ...state, mutate, reset };
}

// ============================================
// Wardrobe Onboarding Hooks
// ============================================

export function useOnboardClothes(): UseMutationReturn<
  { image: File; wardrobeId?: string },
  WardrobeOnboardResponse
> {
  const { userId } = useUser();
  const [state, setState] = useState<UseApiState<WardrobeOnboardResponse>>({
    data: null,
    loading: false,
    error: null,
  });

  const mutate = useCallback(
    async (request: { image: File; wardrobeId?: string }) => {
      if (!userId) {
        setState((prev) => ({ ...prev, error: "User not authenticated" }));
        return null;
      }

      setState({ data: null, loading: true, error: null });

      const response = await onboardClothes(userId, request.image, request.wardrobeId);

      if (isApiSuccess(response)) {
        setState({ data: response.data, loading: false, error: null });
        // Trigger credit refresh after successful AI operation
        triggerCreditRefresh();
        trackEvent("clothes_uploaded", { method: "single" });
        return response.data;
      } else {
        const errorMsg = getApiError(response);
        setState({ data: null, loading: false, error: errorMsg });
        trackError("clothes_upload", errorMsg);
        return null;
      }
    },
    [userId]
  );

  const reset = useCallback(() => {
    setState({ data: null, loading: false, error: null });
  }, []);

  return { ...state, mutate, reset };
}

export function useOnboardClothesBatch(): UseMutationReturn<
  { images: File[]; wardrobeId?: string },
  WardrobeUploadJobResponse
> {
  const { userId } = useUser();
  const [state, setState] = useState<UseApiState<WardrobeUploadJobResponse>>({
    data: null,
    loading: false,
    error: null,
  });

  const mutate = useCallback(
    async (request: { images: File[]; wardrobeId?: string }) => {
      if (!userId) {
        setState((prev) => ({ ...prev, error: "User not authenticated" }));
        return null;
      }

      setState({ data: null, loading: true, error: null });

      const response = await onboardClothesBatch(userId, request.images, request.wardrobeId);

      if (isApiSuccess(response)) {
        setState({ data: response.data, loading: false, error: null });
        // Trigger credit refresh after credits are reserved
        triggerCreditRefresh();
        trackEvent("batch_upload_started", { image_count: request.images.length });
        return response.data;
      } else {
        const errorMsg = getApiError(response);
        setState({ data: null, loading: false, error: errorMsg });
        trackError("batch_upload", errorMsg);
        throw new Error(errorMsg);
      }
    },
    [userId]
  );

  const reset = useCallback(() => {
    setState({ data: null, loading: false, error: null });
  }, []);

  return { ...state, mutate, reset };
}

// ============================================
// Set Detection & Processing Hooks
// ============================================

export function useAnalyzeForSet(): UseMutationReturn<
  { image: File },
  AnalyzeForSetResponse
> {
  const { userId } = useUser();
  const [state, setState] = useState<UseApiState<AnalyzeForSetResponse>>({
    data: null,
    loading: false,
    error: null,
  });

  const mutate = useCallback(
    async (request: { image: File }) => {
      if (!userId) {
        setState((prev) => ({ ...prev, error: "User not authenticated" }));
        return null;
      }

      setState({ data: null, loading: true, error: null });

      const response = await analyzeForSet(userId, request.image);

      if (isApiSuccess(response)) {
        setState({ data: response.data, loading: false, error: null });
        trackEvent("set_detection_analyzed");
        return response.data;
      } else {
        const errorMsg = getApiError(response);
        setState({ data: null, loading: false, error: errorMsg });
        trackError("set_detection", errorMsg);
        throw new Error(errorMsg);
      }
    },
    [userId]
  );

  const reset = useCallback(() => {
    setState({ data: null, loading: false, error: null });
  }, []);

  return { ...state, mutate, reset };
}

export function useConfirmOnboard(): UseMutationReturn<
  { analysisId: string; processAsSet: boolean },
  ConfirmOnboardJobResponse
> {
  const { userId } = useUser();
  const [state, setState] = useState<UseApiState<ConfirmOnboardJobResponse>>({
    data: null,
    loading: false,
    error: null,
  });

  const mutate = useCallback(
    async (request: { analysisId: string; processAsSet: boolean }) => {
      if (!userId) {
        setState((prev) => ({ ...prev, error: "User not authenticated" }));
        return null;
      }

      setState({ data: null, loading: true, error: null });

      const response = await confirmOnboard(
        userId,
        request.analysisId,
        request.processAsSet
      );

      if (isApiSuccess(response)) {
        setState({ data: response.data, loading: false, error: null });
        triggerCreditRefresh();
        trackEvent("set_detection_confirmed", {
          processedAsSet: request.processAsSet,
        });
        return response.data;
      } else {
        const errorMsg = getApiError(response);
        setState({ data: null, loading: false, error: errorMsg });
        trackError("set_detection_confirm", errorMsg);
        return null;
      }
    },
    [userId]
  );

  const reset = useCallback(() => {
    setState({ data: null, loading: false, error: null });
  }, []);

  return { ...state, mutate, reset };
}

// ============================================
// Upload Quota Hook
// ============================================

/**
 * Hook to fetch user's upload quota
 */
export function useUploadQuota(): UseApiReturn<UploadQuota> {
  const [data, setData] = useState<UploadQuota | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchQuota = useCallback(async () => {
    try {
      setLoading(true);
      const response = await getUploadQuota();
      if (response.success && response.data) {
        setData(response.data);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch upload quota');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchQuota();
  }, [fetchQuota]);

  return { data, loading, error, refetch: fetchQuota };
}

// ============================================
// Pods Hooks
// ============================================

/**
 * Hook to fetch user's pods
 */
export function usePods(): UseApiReturn<Pod[]> {
  const [state, setState] = useState<UseApiState<Pod[]>>({
    data: null,
    loading: false,
    error: null,
  });
  const [hasFetched, setHasFetched] = useState(false);

  const refetch = useCallback(async () => {
    setState((prev) => ({ ...prev, loading: true, error: null }));

    // Ensure default pods exist for the user (creates Your Favorites if missing)
    await ensureDefaultPods();

    const response = await getPods();

    if (isApiSuccess(response)) {
      setState({ data: response.data, loading: false, error: null });
    } else {
      setState({ data: null, loading: false, error: getApiError(response) });
    }
  }, []);

  // Only auto-fetch once on mount
  useEffect(() => {
    if (!hasFetched) {
      setHasFetched(true);
      refetch();
    }
  }, [hasFetched, refetch]);

  return { ...state, refetch };
}

/**
 * Hook to fetch a specific pod with items
 */
export function usePod(podName: string | null): UseApiReturn<PodWithItems> {
  const [state, setState] = useState<UseApiState<PodWithItems>>({
    data: null,
    loading: false,
    error: null,
  });

  const refetch = useCallback(async () => {
    if (!podName) return;

    setState({ data: null, loading: true, error: null });

    const response = await getPod(podName);

    if (isApiSuccess(response)) {
      setState({ data: response.data, loading: false, error: null });
    } else {
      setState({ data: null, loading: false, error: getApiError(response) });
    }
  }, [podName]);

  useEffect(() => {
    if (podName) {
      refetch();
    }
  }, [refetch, podName]);

  return { ...state, refetch };
}

/**
 * Hook to create a pod
 */
export function useCreatePod(): UseMutationReturn<string, Pod> {
  const [state, setState] = useState<UseApiState<Pod>>({
    data: null,
    loading: false,
    error: null,
  });

  const mutate = useCallback(async (name: string) => {
    setState({ data: null, loading: true, error: null });

    const response = await apiCreatePod(name);

    if (isApiSuccess(response)) {
      trackPodCreated(name);
      setState({ data: response.data, loading: false, error: null });
      return response.data;
    } else {
      setState({ data: null, loading: false, error: getApiError(response) });
      return null;
    }
  }, []);

  const reset = useCallback(() => {
    setState({ data: null, loading: false, error: null });
  }, []);

  return { ...state, mutate, reset };
}

/**
 * Hook to add item to pod
 */
export function useAddItemToPod(): UseMutationReturn<{ podName: string; itemId: string }, { message: string }> {
  const [state, setState] = useState<UseApiState<{ message: string }>>({
    data: null,
    loading: false,
    error: null,
  });

  const mutate = useCallback(async (request: { podName: string; itemId: string }) => {
    setState({ data: null, loading: true, error: null });

    const response = await apiAddItemToPod(request.podName, request.itemId);

    if (isApiSuccess(response)) {
      trackPodItemAdded(request.podName);
      emitWardrobeMutated();
      setState({ data: response.data, loading: false, error: null });
      return response.data;
    } else {
      setState({ data: null, loading: false, error: getApiError(response) });
      return null;
    }
  }, []);

  const reset = useCallback(() => {
    setState({ data: null, loading: false, error: null });
  }, []);

  return { ...state, mutate, reset };
}

/**
 * Hook to remove item from pod
 */
export function useRemoveItemFromPod(): UseMutationReturn<{ podName: string; itemId: string }, { message: string }> {
  const [state, setState] = useState<UseApiState<{ message: string }>>({
    data: null,
    loading: false,
    error: null,
  });

  const mutate = useCallback(async (request: { podName: string; itemId: string }) => {
    setState({ data: null, loading: true, error: null });

    const response = await apiRemoveItemFromPod(request.podName, request.itemId);

    if (isApiSuccess(response)) {
      trackPodItemRemoved(request.podName);
      emitWardrobeMutated();
      setState({ data: response.data, loading: false, error: null });
      return response.data;
    } else {
      setState({ data: null, loading: false, error: getApiError(response) });
      return null;
    }
  }, []);

  const reset = useCallback(() => {
    setState({ data: null, loading: false, error: null });
  }, []);

  return { ...state, mutate, reset };
}

/**
 * Hook to toggle favorite status (add/remove from "Your Favorites" pod)
 */
export function useToggleFavorite(): UseMutationReturn<string, { isFavorited: boolean; message: string }> {
  const [state, setState] = useState<UseApiState<{ isFavorited: boolean; message: string }>>({
    data: null,
    loading: false,
    error: null,
  });

  const mutate = useCallback(async (itemId: string) => {
    setState({ data: null, loading: true, error: null });

    const response = await apiToggleFavorite(itemId);

    if (isApiSuccess(response)) {
      trackDressFavorited(itemId, response.data.isFavorited);
      emitWardrobeMutated();
      setState({ data: response.data, loading: false, error: null });
      return response.data;
    } else {
      console.error('[useToggleFavorite] Error:', getApiError(response));
      setState({ data: null, loading: false, error: getApiError(response) });
      return null;
    }
  }, []);

  const reset = useCallback(() => {
    setState({ data: null, loading: false, error: null });
  }, []);

  return { ...state, mutate, reset };
}

/**
 * Hook to delete a pod
 */
export function useDeletePod(): UseMutationReturn<string, { message: string }> {
  const [state, setState] = useState<UseApiState<{ message: string }>>({
    data: null,
    loading: false,
    error: null,
  });

  const mutate = useCallback(async (podName: string) => {
    setState({ data: null, loading: true, error: null });

    const response = await apiDeletePod(podName);

    if (isApiSuccess(response)) {
      emitWardrobeMutated();
      setState({ data: response.data, loading: false, error: null });
      return response.data;
    } else {
      setState({ data: null, loading: false, error: getApiError(response) });
      return null;
    }
  }, []);

  const reset = useCallback(() => {
    setState({ data: null, loading: false, error: null });
  }, []);

  return { ...state, mutate, reset };
}

// ============================================
// Catalog Basics Hooks
// ============================================

/**
 * Hook to fetch catalog basics with filters. Catalog is read-only + shared, so
 * no auto-refetch needed once loaded.
 */
export function useCatalogBasics(
  params?: CatalogBasicsParams
): UseApiReturn<CatalogBasicsResponse> {
  const [state, setState] = useState<UseApiState<CatalogBasicsResponse>>({
    data: null,
    loading: false,
    error: null,
  });

  const gender = params?.gender;
  const category = params?.category;
  const colorFamily = params?.colorFamily;
  const sleeves = params?.sleeves;
  const limit = params?.limit;
  const offset = params?.offset;

  const refetch = useCallback(async () => {
    setState((prev) => ({ ...prev, loading: true, error: null }));
    const response = await getCatalogBasics({ gender, category, colorFamily, sleeves, limit, offset });
    if (isApiSuccess(response)) {
      setState({ data: response.data, loading: false, error: null });
    } else {
      setState({ data: null, loading: false, error: getApiError(response) });
    }
  }, [gender, category, colorFamily, sleeves, limit, offset]);

  useEffect(() => {
    refetch();
  }, [refetch]);

  return { ...state, refetch };
}

/**
 * Hook to commit a catalog selection — creates WardrobeItem rows (source=catalog, free).
 */
export function useCommitCatalogSelection(): UseMutationReturn<
  CatalogCommitItem[],
  CatalogCommitResponse
> {
  const [state, setState] = useState<UseApiState<CatalogCommitResponse>>({
    data: null,
    loading: false,
    error: null,
  });

  const mutate = useCallback(async (items: CatalogCommitItem[]) => {
    setState({ data: null, loading: true, error: null });
    const response = await apiCommitCatalogSelection(items);
    if (isApiSuccess(response)) {
      setState({ data: response.data, loading: false, error: null });
      trackEvent("catalog_commit", {
        itemCount: response.data.count,
        uniqueSlugs: items.length,
      });
      return response.data;
    } else {
      const errorMsg = getApiError(response);
      setState({ data: null, loading: false, error: errorMsg });
      trackError("catalog_commit", errorMsg);
      return null;
    }
  }, []);

  const reset = useCallback(() => {
    setState({ data: null, loading: false, error: null });
  }, []);

  return { ...state, mutate, reset };
}

/**
 * Hook to rename a pod
 */
export function useRenamePod(): UseMutationReturn<{ podName: string; newName: string }, Pod> {
  const [state, setState] = useState<UseApiState<Pod>>({
    data: null,
    loading: false,
    error: null,
  });

  const mutate = useCallback(async (request: { podName: string; newName: string }) => {
    setState({ data: null, loading: true, error: null });

    const response = await apiRenamePod(request.podName, request.newName);

    if (isApiSuccess(response)) {
      emitWardrobeMutated();
      setState({ data: response.data, loading: false, error: null });
      return response.data;
    } else {
      setState({ data: null, loading: false, error: getApiError(response) });
      return null;
    }
  }, []);

  const reset = useCallback(() => {
    setState({ data: null, loading: false, error: null });
  }, []);

  return { ...state, mutate, reset };
}
